import { useEffect } from 'react';
import { useFormContext, Controller, FieldError } from 'react-hook-form';
import cn from 'classnames';
import dayjs from 'dayjs';
// Hooks
import { useStreamImageUpload } from 'hooks';
// Constants
import { TIME_ZONE_OPTIONS, TimeZoneOption } from 'constants/timeZone';
// Helpers
import { REPEAT_STREAM_OPTIONS, RepeatStreamOption } from 'helpers/streams';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Ui
import Input from 'ui3/Input/Input';
import TextArea from 'ui3/TextArea/TextArea';
import TagsInput from 'ui3/TagsInput/TagsInput';
import Switch from 'ui3/Switch/Switch';
import DatePicker from 'ui3/DatePicker/DatePicker';
import Select from 'ui3/Select/Select';
import Text from 'ui3/Text/Text';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Components
import SectionDivider from 'components/common3/SectionDivider/SectionDivider';
import UploadImagePlaceholder from 'components/ProfileSetup/components/common/UploadImagePlaceholder/UploadImagePlaceholder';
import EasyImageCrop from 'components/common3/EasyImageCrop/EasyImageCrop';
// Styles
import styles from './StreamDetailsStep.module.scss';

export const NAME_MAX_LENGTH = 200;
export const DESCRIPTION_MAX_LENGTH = 400;

const StreamDetailsStep = (): JSX.Element => {
  const {
    register,
    errors,
    setValue,
    control,
    watch,
    trigger,
  } = useFormContext<FormInputs>();

  useEffect(() => {
    register({ name: 'image' });
    register({ name: 'previewImage' });
  }, [register]);

  const image = watch('image');
  const previewImage = watch('previewImage');
  const timeZone = watch('timeZone');
  const scheduleDate = watch('scheduleDate');

  const {
    getRootProps,
    getInputProps,
    open,
    handleApplyImage,
    handlePreviewImageRemove,
  } = useStreamImageUpload({ setValue });

  const selectedTimeZoneOffset = dayjs
    .tz(
      dayjs(scheduleDate ? dayjs(scheduleDate) : dayjs()).format(
        'YYYY-MM-DD HH:mm'
      ),
      timeZone?.tzName
    )
    .utcOffset();

  const currentTimeInSelectedTimeZone = dayjs()
    .add(selectedTimeZoneOffset, 'minute')
    .add(new Date().getTimezoneOffset(), 'minute')
    .toDate();

  // minDate only disables past days, the time list stays selectable. minTime /
  // maxTime are no help either: they match on time-of-day alone, so they would
  // cut off future days too.
  const isFutureTimeSlot = (time: Date) =>
    dayjs(time).isAfter(currentTimeInSelectedTimeZone);

  return (
    <div className={styles.root}>
      <div className={styles.columns}>
        <div className={styles.left}>
          <div className={styles.section}>
            <Controller
              control={control}
              name="name"
              render={({ onChange, value }) => (
                <Input
                  name="name"
                  label="Event Name"
                  placeholder="Add title"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  maxLength={NAME_MAX_LENGTH}
                  error={errors?.name?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="description"
              render={({ onChange, value }) => (
                <TextArea
                  name="description"
                  label="Description"
                  placeholder="Add description"
                  value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  notice="Tell viewers what to expect from your event/stream."
                  error={errors?.description?.message}
                  rows={4}
                />
              )}
            />
            <Controller
              name="hashtags"
              control={control}
              defaultValue={[]}
              render={({ onChange, value: tags }) => (
                <TagsInput
                  tags={tags || []}
                  label="Tags"
                  placeholder="Add tags here"
                  onChange={onChange}
                  name="hashtags"
                />
              )}
            />
          </div>

          <SectionDivider />

          <div className={styles.section}>
            <div className={styles.sectionHeading}>
              <Text variant="subtitle2">Scheduling Details</Text>
              <Text variant="body2Regular" color="lights-medium">
                Set when your stream goes live and where it takes place
              </Text>
            </div>
            <div className={styles.dateTimezoneContainer}>
              <div className={styles.dateTimezoneField}>
                <Controller
                  control={control}
                  name="scheduleDate"
                  render={({ onChange, onBlur, value }) => (
                    <DatePicker
                      showMonthDropdown
                      showYearDropdown
                      yearDropdownItemNumber={70}
                      selected={value ? dayjs(value).toDate() : null}
                      onBlur={onBlur}
                      onChange={(date) => {
                        onChange(date);
                        trigger('scheduleDate');
                      }}
                      showTimeSelect
                      placeholderText="MM/DD/YYYY 00:00AM/PM"
                      minDate={currentTimeInSelectedTimeZone}
                      filterTime={isFutureTimeSlot}
                      customInput={
                        <Input
                          label="Date"
                          aria-label="Select schedule date"
                          name="dateTimeInput"
                          error={errors?.scheduleDate?.message}
                        />
                      }
                    />
                  )}
                />
              </div>
              <div className={styles.dateTimezoneField}>
                <Controller
                  control={control}
                  name="timeZone"
                  render={({ onChange, value }) => {
                    const matchedOption = TIME_ZONE_OPTIONS.find(
                      (option) => option.tzCode === value?.tzCode
                    );

                    return (
                      <Select
                        aria-label="Select Time Zone"
                        placeholder="Time Zone"
                        label="Time zone"
                        options={TIME_ZONE_OPTIONS}
                        getOptionLabel={(option) => option.label}
                        getOptionValue={(option) => option.tzCode}
                        size="medium"
                        id="timeZone"
                        inputId="tzCodeSelect"
                        error={(errors?.timeZone as any)?.message}
                        onChange={(option: TimeZoneOption) => {
                          onChange(option);
                          // A slot that was in the future in the old zone can be
                          // in the past in the new one.
                          trigger('scheduleDate');
                        }}
                        value={matchedOption}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <Controller
              name="repeatsEveryEnum"
              defaultValue={REPEAT_STREAM_OPTIONS[0]}
              control={control}
              render={({ onChange, ...rest }) => (
                <Select
                  placeholder="Select"
                  label="Does this event repeat?"
                  options={REPEAT_STREAM_OPTIONS}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => `${option.value}`}
                  inputId="repeatsEveryEnum"
                  fieldSize="large"
                  onChange={(data: RepeatStreamOption) => onChange(data)}
                  {...rest}
                />
              )}
            />
          </div>

          <SectionDivider />

          <div className={styles.testStreamRow}>
            <div>
              <Text variant="body2Medium">Test Stream</Text>
              <Text variant="body2Regular" color="lights-low">
                Run this as a test stream before going live to your fans.
              </Text>
            </div>
            <Switch name="isHidden" ref={register} />
          </div>
        </div>
        <div className={styles.uploaderColumn}>
          {image ? (
            <div className={cn(styles.uploader, styles.filled)}>
              <EasyImageCrop
                className={styles.croppedImage}
                imageFile={image}
                onApplyImage={handleApplyImage}
                aspect={1}
              />
              <button
                type="button"
                className={styles.removeImageButton}
                aria-label="Remove image"
                onClick={handlePreviewImageRemove}
              >
                <MillionsIcon name="trash" size={24} />
              </button>
            </div>
          ) : previewImage ? (
            <div className={cn(styles.uploader, styles.filled)}>
              <img
                src={previewImage}
                className={styles.previewImage}
                alt="Stream poster"
              />
              <button
                type="button"
                className={styles.removeImageButton}
                aria-label="Remove image"
                onClick={handlePreviewImageRemove}
              >
                <MillionsIcon name="trash" size={24} />
              </button>
            </div>
          ) : (
            <div
              {...getRootProps()}
              className={cn(styles.uploader, {
                [styles.uploaderError]: Boolean(errors?.previewImage),
              })}
            >
              <input {...getInputProps()} aria-label="upload image" />
              <UploadImagePlaceholder
                title="Drop an image or click to upload"
                description=".png or .jpeg max 50mb"
                onClick={open}
              />
            </div>
          )}
          {errors?.previewImage && image && (
            <Text variant="captionRegular" color="lights-medium">
              Apply your crop to set the poster
            </Text>
          )}
          {errors?.previewImage && !image && (
            <Text
              variant="captionRegular"
              className={styles.uploaderErrorMessage}
              role="alert"
            >
              {(errors.previewImage as FieldError)?.message ||
                'Poster is required'}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamDetailsStep;
